import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import { useAuth } from '../../context/AuthContext';
import * as dbService from '../../services/dbService';
import { supabase } from '../../lib/supabaseClient';
import { generateImage } from '../../services/aiService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as GiIcons from 'react-icons/gi';

const Characters = () => {
  const { user } = useAuth();
  const { state, updateCharacter, removeCollaborator } = useStory();
  const [characters, setCharacters] = useState([]);
  const [allCharacters, setAllCharacters] = useState([]);
  const [pendingRoles, setPendingRoles] = useState({});
  const [characterStats, setCharacterStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stories, setStories] = useState([]);

  const [isEditing, setIsEditing] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);

  const [isCustomGender, setIsCustomGender] = useState(false);
  const [activeTab, setActiveTab] = useState('my-characters'); // 'my-characters' | 'all-characters'
  const [searchQuery, setSearchQuery] = useState('');

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedCharToInvite, setSelectedCharToInvite] = useState(null);
  const [inviteRole, setInviteRole] = useState('Editor');

  const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);

  const isTierRestricted = state.subscriptionTier !== 'Storyteller' && state.subscriptionTier !== 'Worldbuilder';

  useEffect(() => {
    const fetchCharacters = async () => {
      if (user) {
        try {
          const [chars, allChars, stats, userStories, { data: collabs }] = await Promise.all([
             dbService.getCharacters(user.id),
             dbService.getAllCharacters(),
             dbService.getCharacterStoriesInfo(),
             dbService.getUserStories(user.id),
             supabase.from('collaborators').select('character_id, stories(world_genre)').eq('status', 'Accepted')
          ]);

          // Augment stats manually client-side so it works immediately regardless of RPC migration
          const safeCollabs = collabs || [];
          const safeStories = userStories || [];

          const augmentedStats = (allChars || []).map(char => {
             const rpcStat = (stats || []).find(s => s.character_id === char.id || s.character_name === char.name) || { active_count: 0, genres: '' };
             const ownedStories = safeStories.filter(s => s.character_name === char.name);
             const collabStories = safeCollabs.filter(c => c.character_id === char.id);

             const totalActive = Math.max(Number(rpcStat.active_count || 0), ownedStories.length + collabStories.length);
             const allGenres = [...new Set([
                 ...ownedStories.map(s => s.world_genre),
                 ...collabStories.map(c => c.stories?.world_genre),
                 ...(rpcStat.genres ? rpcStat.genres.split(', ') : [])
             ])].filter(Boolean).join(', ');

             return { character_id: char.id, character_name: char.name, active_count: totalActive, genres: allGenres };
          });

          setCharacters(chars || []);
          setAllCharacters(allChars || []);
          setCharacterStats(augmentedStats);
          setStories(userStories || []);
        } catch (error) {
          console.error("Failed to fetch characters", error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchCharacters();

    const channel = supabase.channel('characters-sync')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'characters' }, () => {
        // When any character is updated/inserted/deleted, refetch lists
        fetchCharacters();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleCreateNew = () => {
    setEditingCharacter({
      name: '',
      nickname: '',
      preferred_reference: 'name',
      role: '',
      gender: '',
      appearance: '',
      backstory: '',
      avatar: '',
      traits: [],
      equipment: []
    });
    setIsCustomGender(false);
    setIsEditing(true);
  };

  const handleEdit = (char) => {
    setEditingCharacter({ ...char, preferred_reference: char.preferred_reference || 'name' });
    setIsCustomGender(char.gender && !['Male', 'Female', 'Non-binary', ''].includes(char.gender));
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      if (editingCharacter.id) {
        // Update existing
        const updated = await dbService.updateCharacterDB(editingCharacter.id, {
          name: editingCharacter.name,
          nickname: editingCharacter.nickname,
          preferred_reference: editingCharacter.preferred_reference || 'name',
          role: editingCharacter.role,
          gender: editingCharacter.gender,
          appearance: editingCharacter.appearance,
          backstory: editingCharacter.backstory,
          avatar: editingCharacter.avatar,
          traits: typeof editingCharacter.traits === 'string' ? editingCharacter.traits.split(',').map(t => t.trim()).filter(Boolean) : editingCharacter.traits,
          equipment: typeof editingCharacter.equipment === 'string' ? editingCharacter.equipment.split(',').map(t => t.trim()).filter(Boolean) : editingCharacter.equipment
        });
        setCharacters(characters.map(c => c.id === updated.id ? updated : c));
        if (state.character && state.character.id === updated.id) {
           updateCharacter(updated);
        }
      } else {
        // Create new
        const created = await dbService.createCharacter(user.id, {
          name: editingCharacter.name,
          nickname: editingCharacter.nickname,
          preferred_reference: editingCharacter.preferred_reference || 'name',
          role: editingCharacter.role,
          gender: editingCharacter.gender,
          appearance: editingCharacter.appearance,
          backstory: editingCharacter.backstory,
          avatar: editingCharacter.avatar,
          traits: typeof editingCharacter.traits === 'string' ? editingCharacter.traits.split(',').map(t => t.trim()).filter(Boolean) : editingCharacter.traits,
          equipment: typeof editingCharacter.equipment === 'string' ? editingCharacter.equipment.split(',').map(t => t.trim()).filter(Boolean) : editingCharacter.equipment
        });
        setCharacters([created, ...characters]);
      }
      setIsEditing(false);
      setEditingCharacter(null);
    } catch (error) {
      console.error("Failed to save character", error);
      alert("Failed to save character");
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this character?")) {
      try {
        await dbService.deleteCharacter(id);
        setCharacters(characters.filter(c => c.id !== id));
        // Note: we might want to handle the case where the deleted character is the active one.
      } catch (error) {
        console.error("Failed to delete character", error);
        alert("Failed to delete character");
      }
    }
  };

  const handleGenderChange = (e) => {
    const val = e.target.value;
    if (val === 'Other') {
      setIsCustomGender(true);
      setEditingCharacter({ ...editingCharacter, gender: '' });
    } else {
      setIsCustomGender(false);
      setEditingCharacter({ ...editingCharacter, gender: val });
    }
  };

  const handleGenerateAvatar = async () => {
    if (!editingCharacter.appearance) {
      alert("Please provide appearance details first.");
      return;
    }
    setIsGeneratingAvatar(true);
    try {
      const url = await generateImage(
        "A character portrait.",
        editingCharacter,
        "realistic",
        []
      );
      setEditingCharacter({ ...editingCharacter, avatar: url });
    } catch (e) {
      console.error(e);
      alert("Failed to generate avatar.");
    } finally {
      setIsGeneratingAvatar(false);
    }
  };

  const handleUploadAvatar = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingCharacter({ ...editingCharacter, avatar: reader.result });
      };
      reader.readAsDataURL(file);
      e.target.value = null; // reset input
    }
  };

  const openInviteModal = (char) => {
    setSelectedCharToInvite(char);
    setIsInviteModalOpen(true);
  };

  const handleAcceptRequest = async (collabId, userId, storyId) => {
    const role = pendingRoles[collabId] || 'Co-Author';
    try {
      await dbService.acceptCollaborationWithRole(storyId, userId, role, state.ai?.settings?.isPaused || false);
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert('Failed to accept request.');
    }
  };

  const handleDeclineRequest = async (collabId) => {
    try {
      if (collabId && typeof collabId !== 'number') await dbService.deleteCollaborator(collabId);
      removeCollaborator(collabId);
    } catch (e) {
      console.error(e);
      alert('Failed to decline request.');
    }
  };

  const handleInviteToStory = async (storyId) => {
    const roleToUse = inviteRole;
    if (!selectedCharToInvite || !user) return;
    try {
      if (selectedCharToInvite.user_id === user.id) {
         // The user is adding their own character to their story
         // Right now, story logic looks at character_nickname, so we can update the story directly
         await supabase.from('stories').update({ character_nickname: selectedCharToInvite.nickname || selectedCharToInvite.name }).eq('id', storyId);
         alert(`Added ${selectedCharToInvite.name} to the story!`);
      } else {
         // Invite another user to collaborate
         await supabase.from('collaborators').insert({
            story_id: storyId,
            user_id: selectedCharToInvite.user_id,
            role: roleToUse,
            character_id: selectedCharToInvite.id,
            ai_paused: state.ai?.settings?.isPaused || false
         });
         alert(`Invited ${selectedCharToInvite.name}'s owner to collaborate!`);
      }
      setIsInviteModalOpen(false);
      setSelectedCharToInvite(null);
    } catch (e) {
      console.error(e);
      alert("Failed to add/invite.");
    }
  };

  if (isLoading) {
    return <div className="p-12 text-center text-gray-900 dark:text-white flex justify-center items-center h-full"><SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-4xl text-amber-500" /></div>;
  }

  const filteredPlatformCharacters = allCharacters.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {!isEditing ? (
        <>
          <div className="flex justify-between items-center mb-6 border-b border-gray-100 dark:border-purple-800 pb-6">
            <div>
              <h1 className="text-4xl font-serif font-bold text-gray-900 dark:text-white">Character Directory</h1>
              <p className="text-gray-900 dark:text-purple-400 mt-2">Manage character dossiers and see which stories they are a part of.</p>
            </div>
            <button onClick={handleCreateNew} className="btn-primary flex items-center space-x-2">
              <SafeIcon icon={FiIcons.FiPlus} />
              <span>New Character</span>
            </button>
          </div>

          {/* Pending Requests Section */}
          {(state.collaborators || []).filter(c => c.status === 'Pending').length > 0 && state.ownerId === user?.id && (
            <div className="mb-6 space-y-2">
              <h5 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest border-b border-gray-100 dark:border-purple-800 pb-2">Pending Join Requests</h5>
              {state.collaborators.filter(c => c.status === 'Pending').map(c => (
                <motion.div layout key={c.id} className="flex items-center justify-between p-3 bg-amber-50/50 dark:bg-amber-900/10 rounded-xl border border-amber-200 dark:border-amber-800/50">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-red-500 flex items-center justify-center text-white text-xs font-bold">
                      {(c.name || c.profiles?.username || c.characters?.name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900 dark:text-white">{c.name || c.profiles?.username || c.characters?.name || 'Unknown User'}</p>
                      <span className="text-[9px] font-bold text-amber-600">Requested to Join</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={pendingRoles[c.id] || 'Co-Author'}
                      onChange={(e) => setPendingRoles(prev => ({ ...prev, [c.id]: e.target.value }))}
                      className="text-[10px] py-1 px-2 rounded-lg border border-gray-200 dark:border-purple-800 bg-white dark:bg-purple-900"
                    >
                      <option value="Co-Author">Co-Author</option>
                      <option value="Editor">Editor</option>
                      <option value="Viewer">Viewer</option>
                    </select>
                    <button onClick={() => handleAcceptRequest(c.id, c.user_id, c.story_id)} className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-lg font-bold hover:bg-green-200 transition-colors">Accept</button>
                    <button onClick={() => handleDeclineRequest(c.id)} className="text-[10px] bg-red-100 text-red-700 px-2 py-1 rounded-lg font-bold hover:bg-red-200 transition-colors">Decline</button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between items-center mb-8 space-y-4 sm:space-y-0">
            <div className="flex space-x-4">
              <button
                className={`px-4 py-2 font-bold rounded-lg transition-colors ${activeTab === 'my-characters' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-900 dark:bg-purple-900/50 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800'}`}
                onClick={() => setActiveTab('my-characters')}
              >
                My Characters
              </button>
              <button
                className={`px-4 py-2 font-bold rounded-lg transition-colors ${activeTab === 'all-characters' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-900 dark:bg-purple-900/50 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800'}`}
                onClick={() => setActiveTab('all-characters')}
              >
                All Platform Characters
              </button>
            </div>
            {activeTab === 'all-characters' && (
              <div className="w-full sm:w-64 relative">
                <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-900 dark:text-purple-400" />
                <input
                  type="text"
                  placeholder="Search characters..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-purple-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}
          </div>

          {activeTab === 'my-characters' && characters.length === 0 && (
            <div className="p-8 text-center bg-gray-50 dark:bg-purple-900/20 rounded-2xl border border-dashed border-gray-200 dark:border-purple-800">
              <SafeIcon icon={FiIcons.FiUser} className="text-4xl text-gray-900 dark:text-purple-700 mx-auto mb-4" />
              <p className="text-gray-900 dark:text-purple-300">You haven't created any characters yet.</p>
              <button onClick={handleCreateNew} className="text-amber-600 hover:text-amber-500 font-bold mt-2">Forge your first avatar &rarr;</button>
            </div>
          )}

          {activeTab === 'my-characters' && characters.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {characters.map(char => (
                <div key={char.id} className="block group text-left w-full h-full p-6 bg-white dark:bg-purple-950/50 rounded-2xl border border-gray-100 dark:border-purple-800 shadow-sm transition-all relative flex flex-col">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white line-clamp-1 pr-6">{char.name || 'Unnamed'}</h3>
                    <div className="absolute top-6 right-6 z-10 flex space-x-2">
                      <button
                        onClick={() => handleEdit(char)}
                        className="text-gray-900 hover:text-amber-500 transition-colors dark:text-purple-300"
                        title="Edit character"
                      >
                        <SafeIcon icon={FiIcons.FiEdit2} />
                      </button>
                      <button
                        onClick={(e) => handleDelete(e, char.id)}
                        className="text-gray-900 hover:text-red-500 transition-colors dark:text-purple-300"
                        title="Delete character"
                      >
                        <SafeIcon icon={FiIcons.FiTrash2} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-amber-600 font-medium mb-4">{char.role || 'No role specified'}</p>

                  {stories.filter(s => s.character_name === char.name).length > 0 && (
                     <div className="mb-4 text-xs">
                        <span className="font-bold text-gray-900 dark:text-purple-300">Story Titles:</span>
                        <ul className="list-disc pl-4 text-slate-600 dark:text-slate-400">
                           {stories.filter(s => s.character_name === char.name).map(s => (
                              <li key={s.id} className="truncate">{s.title || 'Untitled Story'}</li>
                           ))}
                        </ul>
                     </div>
                  )}

                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-purple-900/50 flex-shrink-0 overflow-hidden border border-gray-200 dark:border-purple-800">
                      {char.avatar ? (
                        <img src={char.avatar} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-900 dark:text-purple-700">
                          <SafeIcon icon={FiIcons.FiUser} className="text-2xl" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 dark:text-purple-400 line-clamp-3 flex-grow">
                      {char.appearance || 'No appearance description provided.'}
                    </p>
                  </div>

                  <div className="mt-auto pt-4 border-t border-purple-50 dark:border-purple-900/50 flex justify-between items-center">
                    <span className="text-xs text-gray-900 dark:text-purple-500">
                      {new Date(char.updated_at).toLocaleDateString()}
                    </span>
                    <div className="flex space-x-2">
                       <button
                         onClick={() => openInviteModal(char)}
                         className="text-xs font-bold px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full hover:bg-amber-200 dark:hover:bg-amber-800/50 transition-colors flex items-center space-x-1"
                       >
                         <SafeIcon icon={FiIcons.FiPlus} />
                         <span>Add to Story</span>
                       </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'all-characters' && filteredPlatformCharacters.length === 0 && (
            <div className="p-8 text-center bg-gray-50 dark:bg-purple-900/20 rounded-2xl border border-dashed border-gray-200 dark:border-purple-800">
              <SafeIcon icon={FiIcons.FiUsers} className="text-4xl text-gray-900 dark:text-purple-700 mx-auto mb-4" />
              <p className="text-gray-900 dark:text-purple-300">No characters found matching your search.</p>
            </div>
          )}

          {activeTab === 'all-characters' && filteredPlatformCharacters.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPlatformCharacters.map(char => {
                const stats = characterStats.find(s => s.character_id === char.id || s.character_name === char.name) || { active_count: 0, genres: '' };
                return (
                <div key={char.id} className="block group text-left w-full h-full p-6 bg-white dark:bg-purple-950/50 rounded-2xl border border-gray-100 dark:border-purple-800 shadow-sm transition-all relative flex flex-col">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white line-clamp-1 pr-6">{char.name || 'Unnamed'}</h3>
                    <div className="text-xs text-gray-800 dark:text-purple-400 font-bold bg-gray-100 dark:bg-purple-900/50 px-2 py-1 rounded-full">
                      @{char.profiles?.username || 'Unknown'}
                    </div>
                  </div>
                  <p className="text-sm text-amber-600 font-medium mb-4">{char.role || 'No role specified'}</p>

                  <div className="mb-4 text-xs space-y-1">
                     <p className="text-gray-900 dark:text-purple-300"><span className="font-bold">Active in:</span> {stats.active_count || 0} stories</p>
                     {stats.genres && (
                       <p className="text-gray-900 dark:text-purple-300"><span className="font-bold">Genres:</span> {stats.genres}</p>
                     )}
                  </div>

                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-purple-900/50 flex-shrink-0 overflow-hidden border border-gray-200 dark:border-purple-800">
                      {char.avatar ? (
                        <img src={char.avatar} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-900 dark:text-purple-700">
                          <SafeIcon icon={FiIcons.FiUser} className="text-2xl" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 dark:text-purple-400 line-clamp-3 flex-grow">
                      {char.appearance || 'No appearance description provided.'}
                    </p>
                  </div>

                  <div className="mt-auto pt-4 border-t border-purple-50 dark:border-purple-900/50 flex justify-end">
                    {char.user_id !== user?.id && (
                      <button
                        onClick={() => openInviteModal(char)}
                        className="text-xs font-bold px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full hover:bg-amber-200 dark:hover:bg-amber-800/50 transition-colors flex items-center space-x-1"
                      >
                        <SafeIcon icon={FiIcons.FiMail} />
                        <span>Invite to Collab</span>
                      </button>
                    )}
                  </div>
                </div>
              )})}
            </div>
          )}

          {isInviteModalOpen && selectedCharToInvite && (
             <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-purple-800">
                   <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                     {selectedCharToInvite.user_id === user?.id ? `Add ${selectedCharToInvite.name}` : `Invite ${selectedCharToInvite.name}`}
                   </h3>
                   <p className="text-sm text-gray-900 dark:text-purple-300 mb-6">
                     {selectedCharToInvite.user_id === user?.id
                       ? `Select a story to add your character to.`
                       : `Select a story to invite this character (and their owner, @${selectedCharToInvite.profiles?.username}) to collaborate on.`}
                   </p>


                   <div className="mb-4">
                      <label className="text-sm font-bold text-gray-900 dark:text-purple-200 block mb-2">Select Role:</label>
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        className="input-field text-sm py-2"
                      >
                        <option value="Co-Author">Co-Author</option>
                        <option value="Editor">Editor</option>
                        <option value="Viewer">Viewer</option>
                      </select>
                   </div>

                   {stories.length === 0 ? (
                      <p className="text-sm text-amber-600 mb-4">You don't have any active stories to invite them to.</p>
                   ) : (
                      <ul className="max-h-60 overflow-y-auto space-y-2 mb-6">
                         {stories.map(story => (
                            <li key={story.id}>
                               <button
                                  onClick={() => handleInviteToStory(story.id)}
                                  className="w-full text-left p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-purple-900/30 border border-transparent hover:border-gray-200 dark:hover:border-purple-800 transition-colors"
                               >
                                  <span className="font-bold text-gray-900 dark:text-white block">{story.title || 'Untitled Story'}</span>
                                  <span className="text-xs text-gray-800 dark:text-purple-400">Genre: {story.world_genre || 'Unspecified'}</span>
                               </button>
                            </li>
                         ))}
                      </ul>
                   )}

                   <div className="flex justify-end">
                      <button
                         onClick={() => { setIsInviteModalOpen(false); setSelectedCharToInvite(null); }}
                         className="btn-secondary"
                      >
                         Cancel
                      </button>
                   </div>
                </div>
             </div>
          )}

        </>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 max-w-4xl mx-auto">
          <div className="flex justify-between items-center border-b border-gray-100 dark:border-purple-800 pb-6">
            <h1 className="text-3xl font-serif font-bold text-gray-900 dark:text-white">
              {editingCharacter.id ? 'Edit Character' : 'New Character'}
            </h1>
            <button onClick={() => setIsEditing(false)} className="btn-secondary flex items-center space-x-2 text-sm">
              <SafeIcon icon={FiIcons.FiX} />
              <span>Cancel</span>
            </button>
          </div>

          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-1 space-y-6">
              <div className="aspect-square bg-gray-50 dark:bg-purple-900/40 rounded-3xl border-2 border-dashed border-gray-200 dark:border-purple-800 flex flex-col items-center justify-center relative overflow-hidden group">
                {editingCharacter.avatar ? (
                  <img src={editingCharacter.avatar} alt="Character Avatar" className="w-full h-full object-cover" />
                ) : (
                  <SafeIcon icon={FiIcons.FiUser} className="text-6xl text-gray-900 dark:text-purple-800 group-hover:text-amber-500 transition-colors" />
                )}
                <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center space-y-2">
                  {!isTierRestricted && (
                    <button type="button" onClick={handleGenerateAvatar} disabled={isGeneratingAvatar} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg transition-colors z-10 flex items-center space-x-2">
                      <SafeIcon icon={isGeneratingAvatar ? FiIcons.FiLoader : FiIcons.FiImage} className={isGeneratingAvatar ? 'animate-spin' : ''} />
                      <span>{isGeneratingAvatar ? 'Generating...' : 'Visualize'}</span>
                    </button>
                  )}
                  <label className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full text-xs font-bold shadow-lg transition-colors z-10 flex items-center space-x-2 cursor-pointer">
                    <SafeIcon icon={FiIcons.FiUpload} />
                    <span>Upload Avatar</span>
                    <input type="file" accept="image/*" onChange={handleUploadAvatar} className="hidden" />
                  </label>
                </div>
              </div>

              <div className="p-4 mb-4 bg-gray-50 dark:bg-purple-900/20 border border-gray-200 dark:border-purple-800 rounded-2xl space-y-4">
                 <h3 className="text-xs font-bold text-gray-900 dark:text-purple-300 uppercase flex items-center space-x-2">
                   <SafeIcon icon={FiIcons.FiTag} />
                   <span>Traits (comma separated)</span>
                 </h3>
                 <input
                   value={typeof editingCharacter.traits === 'string' ? editingCharacter.traits : (editingCharacter.traits || []).join(', ')}
                   onChange={(e) => setEditingCharacter({ ...editingCharacter, traits: e.target.value })}
                   className="input-field text-sm"
                   placeholder="e.g. Brave, Resourceful"
                 />
              </div>

              <div className="p-4 mb-4 bg-gray-50 dark:bg-purple-900/20 border border-gray-200 dark:border-purple-800 rounded-2xl space-y-4">
                 <h3 className="text-xs font-bold text-gray-900 dark:text-purple-300 uppercase flex items-center space-x-2">
                   <SafeIcon icon={GiIcons.GiPlainDagger} />
                   <span>Equipment (comma separated)</span>
                 </h3>
                 <input
                   value={typeof editingCharacter.equipment === 'string' ? editingCharacter.equipment : (editingCharacter.equipment || []).join(', ')}
                   onChange={(e) => setEditingCharacter({ ...editingCharacter, equipment: e.target.value })}
                   className="input-field text-sm"
                   placeholder="e.g. Sword, Shield"
                 />
              </div>
            </div>

            <div className="md:col-span-2 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <label className="label-text">Full Name</label>
                  <input
                    value={editingCharacter.name}
                    onChange={(e) => setEditingCharacter({ ...editingCharacter, name: e.target.value })}
                    className="input-field font-serif text-xl"
                    required
                  />
                </div>
                <div className="space-y-4">
                  <label className="label-text">Nickname</label>
                  <input
                    value={editingCharacter.nickname || ''}
                    onChange={(e) => setEditingCharacter({ ...editingCharacter, nickname: e.target.value })}
                    className="input-field font-serif text-xl"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <label className="label-text">Preferred Story Reference</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preferred_reference"
                      value="name"
                      checked={editingCharacter.preferred_reference !== 'nickname'}
                      onChange={(e) => setEditingCharacter({ ...editingCharacter, preferred_reference: e.target.value })}
                      className="form-radio text-gray-800 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-purple-200">Full Name</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preferred_reference"
                      value="nickname"
                      checked={editingCharacter.preferred_reference === 'nickname'}
                      onChange={(e) => setEditingCharacter({ ...editingCharacter, preferred_reference: e.target.value })}
                      className="form-radio text-gray-800 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-purple-200">Nickname</span>
                  </label>
                </div>
              </div>
              <div className="space-y-4">
                <label className="label-text">Narrative Role</label>
                <input
                  value={editingCharacter.role}
                  onChange={(e) => setEditingCharacter({ ...editingCharacter, role: e.target.value })}
                  className="input-field font-bold"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <label className="label-text">Gender</label>
                  <select
                    value={isCustomGender ? 'Other' : editingCharacter.gender || ''}
                    onChange={handleGenderChange}
                    className="input-field"
                  >
                    <option value="" disabled>Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Non-binary">Non-binary</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {isCustomGender && (
                  <div className="space-y-4">
                    <label className="label-text">Custom Gender</label>
                    <input
                      type="text"
                      value={editingCharacter.gender}
                      onChange={(e) => setEditingCharacter({ ...editingCharacter, gender: e.target.value })}
                      className="input-field font-bold"
                      placeholder="Specify gender"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <label className="label-text">Appearance</label>
                <textarea
                  value={editingCharacter.appearance}
                  onChange={(e) => setEditingCharacter({ ...editingCharacter, appearance: e.target.value })}
                  className="input-field min-h-[100px] leading-relaxed"
                />
              </div>
              <div className="space-y-4">
                <label className="label-text">Backstory</label>
                <textarea
                  value={editingCharacter.backstory}
                  onChange={(e) => setEditingCharacter({ ...editingCharacter, backstory: e.target.value })}
                  className="input-field min-h-[150px] leading-relaxed"
                />
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-purple-800">
                <button type="submit" className="btn-primary py-2 px-8 flex items-center space-x-2">
                  <SafeIcon icon={FiIcons.FiSave} />
                  <span>Save Character</span>
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      )}
    </div>
  );
};

export default Characters;
